import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type RefCallback,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Eye,
  BookOpen,
  RotateCcw,
  Flame,
  Wand2,
  Users2,
  GraduationCap,
  TrendingUp,
  Brain,
  Target,
  ChevronRight,
  Home,
  Lightbulb,
  Compass,
  Layers,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useZoneSidebar } from "../../context/ZoneSidebarContext";
import { useIsMobile } from "../../hooks/use-mobile";
import { useDirection } from "../../hooks/useDirection";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
import { LearnTabContent } from "./LearnTabContent";

/* ─── Types ──────────────────────────────────────────── */

interface SectionDef {
  id: string;
  labelKey: string;
  icon: LucideIcon;
}

interface MarketingCategory {
  _id: string;
  name: string | { en: string; he?: string; de?: string };
  slug: string;
  productCount?: number;
}

/* ─── Section definitions ────────────────────────────── */

const SECTIONS: SectionDef[] = [
  { id: "hero", labelKey: "gh_nav_welcome", icon: Home },
  { id: "gap", labelKey: "gh_nav_the_gap", icon: Lightbulb },
  { id: "method", labelKey: "gh_nav_method", icon: Compass },
  { id: "explore", labelKey: "gh_nav_explore", icon: Layers },
  { id: "grow", labelKey: "gh_nav_grow", icon: TrendingUp },
  { id: "learn", labelKey: "home_tab_learn", icon: BookOpen },
  { id: "start", labelKey: "gh_nav_start", icon: Zap },
];

const METHOD_STEPS = [
  { titleKey: "gh_step_1_title", descKey: "gh_step_1_desc", icon: Eye, color: "text-primary", bg: "bg-primary/10", ring: "ring-primary/20" },
  { titleKey: "gh_step_2_title", descKey: "gh_step_2_desc", icon: BookOpen, color: "text-emerald-600", bg: "bg-emerald-100", ring: "ring-emerald-200" },
  { titleKey: "gh_step_3_title", descKey: "gh_step_3_desc", icon: Target, color: "text-violet-600", bg: "bg-violet-100", ring: "ring-violet-200" },
  { titleKey: "gh_step_4_title", descKey: "gh_step_4_desc", icon: RotateCcw, color: "text-amber-600", bg: "bg-amber-100", ring: "ring-amber-200" },
  { titleKey: "gh_step_5_title", descKey: "gh_step_5_desc", icon: Flame, color: "text-rose-600", bg: "bg-rose-100", ring: "ring-rose-200" },
];

const STATS = [
  { valueKey: "gh_gap_stat_1_value", labelKey: "gh_gap_stat_1_label", sourceKey: "gh_gap_stat_1_source" },
  { valueKey: "gh_gap_stat_2_value", labelKey: "gh_gap_stat_2_label", sourceKey: "gh_gap_stat_2_source" },
  { valueKey: "gh_gap_stat_3_value", labelKey: "gh_gap_stat_3_label", sourceKey: "gh_gap_stat_3_source" },
];

const CAPABILITIES = [
  { titleKey: "gh_cap_create_title", descKey: "gh_cap_create_desc", ctaKey: "gh_cap_create_cta", icon: Wand2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", href: "/marketplace" },
  { titleKey: "gh_cap_collab_title", descKey: "gh_cap_collab_desc", ctaKey: "gh_cap_collab_cta", icon: Users2, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200", href: "/spaces" },
  { titleKey: "gh_cap_mentor_title", descKey: "gh_cap_mentor_desc", ctaKey: "gh_cap_mentor_cta", icon: GraduationCap, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", href: "/marketplace?tab=services" },
];

/* ─── Framer Motion variants ─────────────────────────── */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.08 } },
  viewport: { once: true, amount: 0.15 },
};

const staggerChild = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  viewport: { once: true },
};

/* ─── SectionNav (portaled into ZoneSidebar) ─────────── */

interface SectionNavProps {
  sections: SectionDef[];
  activeSection: string;
  collapsed: boolean;
  isRTL: boolean;
  onNavigate: (id: string) => void;
}

const SectionNav = ({ sections, activeSection, collapsed, isRTL, onNavigate }: SectionNavProps) => {
  const { t } = useTranslation();
  const tooltipSide = isRTL ? "left" as const : "right" as const;

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <nav className="py-3 flex flex-col items-center gap-0.5">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <Tooltip key={section.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onNavigate(section.id)}
                    className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center transition-all",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side={tooltipSide}>
                  {t(section.labelKey)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      </TooltipProvider>
    );
  }

  return (
    <nav className="py-2">
      {sections.map((section) => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        return (
          <button
            key={section.id}
            onClick={() => onNavigate(section.id)}
            className={cn(
              "w-full text-start px-5 py-2.5 text-sm transition-all flex items-center gap-2.5 relative",
              isActive
                ? "text-primary font-medium bg-primary/5/80"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80"
            )}
          >
            {isActive && (
              <div className={cn(
                "absolute top-0 bottom-0 w-0.5 bg-primary rounded-e",
                isRTL ? "right-0" : "left-0"
              )} />
            )}
            <Icon className={cn("w-4 h-4 shrink-0", isActive ? "text-primary" : "text-gray-400")} />
            <span className="truncate">{t(section.labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
};

/* ─── GuestHome ──────────────────────────────────────── */

const GuestHome = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isRTL } = useDirection();
  const retryFetch = useRetryFetch();
  const { portalContainer, collapsed: zoneSidebarCollapsed } = useZoneSidebar();

  // Active section tracking
  const [activeSection, setActiveSection] = useState("hero");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionElements = useRef<Map<string, HTMLElement>>(new Map());

  // Marketing categories
  const [categories, setCategories] = useState<MarketingCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Ref callback factory for sections
  const sectionRef: (id: string) => RefCallback<HTMLElement> = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) sectionElements.current.set(id, el);
      else sectionElements.current.delete(id);
    },
    []
  );

  // IntersectionObserver for active section
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting section
        let topEntry: IntersectionObserverEntry | null = null;
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.isIntersecting) {
            if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
              topEntry = entry;
            }
          }
        }
        if (topEntry) {
          const id = topEntry.target.getAttribute("data-section");
          if (id) setActiveSection(id);
        }
      },
      {
        root: container,
        // Bias toward the top 40% of the scroll container
        rootMargin: "0px 0px -60% 0px",
        threshold: 0,
      }
    );

    // Observe all registered section elements
    const timer = setTimeout(() => {
      sectionElements.current.forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // Fetch marketing categories
  useEffect(() => {
    const load = async () => {
      try {
        const query = new URLSearchParams();
        query.set("language", i18n.language || "en");
        const response = await retryFetch(
          `${config.API_BASE}/api/marketing-categories?${query.toString()}`,
          { method: "GET", credentials: "include" }
        );
        if (response.ok) {
          const data = await response.json();
          setCategories(data.list || []);
        }
      } catch {
        // Silently fail — explore section shows nothing
      } finally {
        setCategoriesLoading(false);
      }
    };
    load();
  }, [i18n.language]);

  const getLocalizedName = (name: string | { en: string; he?: string; de?: string }): string => {
    if (typeof name === "string") return name;
    if (typeof name === "object" && name !== null) {
      const lang = i18n.language || "en";
      if (lang === "he" && name.he) return name.he;
      if (lang === "de" && name.de) return name.de;
      return name.en || "";
    }
    return "";
  };

  const scrollToSection = useCallback((id: string) => {
    const el = sectionElements.current.get(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scroll-smooth">
      {/* ── Sidebar Portal ── */}
      {portalContainer && !isMobile && createPortal(
        <SectionNav
          sections={SECTIONS}
          activeSection={activeSection}
          collapsed={zoneSidebarCollapsed}
          isRTL={isRTL}
          onNavigate={scrollToSection}
        />,
        portalContainer
      )}

      {/* ═══════════════════════════════════════════════════
          SECTION 1 — HERO
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("hero")}
        data-section="hero"
        id="hero"
        className="relative overflow-hidden"
      >
        {/* Subtle gradient backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5/80 via-indigo-50/40 to-violet-50/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(99,102,241,0.08)_0%,_transparent_60%)]" />

        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-28 text-center">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/8 border border-primary/10 text-primary text-xs font-medium mb-6 tracking-wide">
              <Sparkles className="w-3.5 h-3.5" />
              {t("gh_hero_badge")}
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.08 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-[1.15] tracking-tight mb-5">
              {t("gh_hero_title_before")}{" "}
              <span className="bg-gradient-to-r from-primary via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                {t("gh_hero_title_highlight")}
              </span>
            </h1>
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.16 }}
            className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto mb-9 leading-relaxed"
          >
            {t("gh_hero_subtitle")}
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.24 }}
            className="flex items-center justify-center gap-3"
          >
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="px-7 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-shadow"
            >
              {t("gh_hero_cta")}
              <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
            >
              {t("sign_in")}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 2 — THE KNOWING-DOING GAP
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("gap")}
        data-section="gap"
        id="gap"
        className="border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t("gh_gap_title")}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
              {t("gh_gap_lead")}
            </p>
          </motion.div>

          {/* Stats row */}
          <motion.div
            {...staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10"
          >
            {STATS.map((stat, i) => (
              <motion.div
                key={i}
                {...staggerChild}
                className="group relative bg-white rounded-2xl border border-gray-100 p-6 text-center hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
                  {t(stat.valueKey)}
                </div>
                <p className="text-sm text-gray-500 leading-snug mb-2">
                  {t(stat.labelKey)}
                </p>
                <p className="text-[11px] text-gray-300 font-medium tracking-wide uppercase">
                  {t(stat.sourceKey)}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            {...fadeUp}
            className="text-center text-gray-600 max-w-2xl mx-auto leading-relaxed text-[15px]"
          >
            {t("gh_gap_bridge")}
          </motion.p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 3 — HOW IT WORKS (5-STEP METHOD)
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("method")}
        data-section="method"
        id="method"
        className="bg-gray-50/70 border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t("gh_method_title")}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              {t("gh_method_subtitle")}
            </p>
          </motion.div>

          {/* Steps — horizontal on desktop, vertical on mobile */}
          <motion.div
            {...staggerContainer}
            className="relative grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4"
          >
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-7 left-[10%] right-[10%] h-px bg-gradient-to-r from-blue-200 via-violet-200 to-rose-200" />

            {METHOD_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={i}
                  {...staggerChild}
                  className="flex flex-col items-center text-center relative"
                >
                  <div className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ring-4 ring-offset-2 ring-offset-gray-50 transition-transform hover:scale-105",
                    step.bg,
                    step.ring
                  )}>
                    <Icon className={cn("w-6 h-6", step.color)} />
                  </div>
                  <div className={cn("text-sm font-semibold text-gray-900 mb-1")}>
                    {t(step.titleKey)}
                  </div>
                  <p className="text-xs text-gray-400 leading-snug max-w-[160px]">
                    {t(step.descKey)}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 4 — EXPLORE THE MARKETPLACE
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("explore")}
        data-section="explore"
        id="explore"
        className="border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t("gh_explore_title")}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              {t("gh_explore_subtitle")}
            </p>
          </motion.div>

          {categoriesLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : categories.length > 0 ? (
            <motion.div
              {...staggerContainer}
              className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8"
            >
              {categories.map((cat) => (
                <motion.button
                  key={cat._id}
                  {...staggerChild}
                  onClick={() => navigate(`/marketplace/category/${cat.slug}`)}
                  className="group text-start rounded-xl border border-gray-100 bg-white p-5 hover:border-primary/30 hover:shadow-md hover:shadow-primary/5 transition-all"
                >
                  <div className="text-sm font-semibold text-gray-800 group-hover:text-primary transition-colors mb-1">
                    {getLocalizedName(cat.name)}
                  </div>
                  {cat.productCount != null && cat.productCount > 0 && (
                    <div className="text-xs text-gray-400">
                      {cat.productCount} {t("gh_explore_activities")}
                    </div>
                  )}
                  <ChevronRight className={cn(
                    "w-4 h-4 text-gray-300 group-hover:text-primary/60 mt-2 transition-all group-hover:translate-x-0.5",
                    isRTL && "rotate-180 group-hover:-translate-x-0.5"
                  )} />
                </motion.button>
              ))}
            </motion.div>
          ) : null}

          <motion.div {...fadeUp} className="text-center">
            <Button
              variant="outline"
              onClick={() => navigate("/marketplace")}
              className="group"
            >
              {t("gh_explore_browse_all")}
              <ArrowRight className={cn(
                "w-4 h-4 ms-2 transition-transform group-hover:translate-x-0.5",
                isRTL && "rotate-180 group-hover:-translate-x-0.5"
              )} />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 5 — THREE WAYS TO GROW
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("grow")}
        data-section="grow"
        id="grow"
        className="bg-gray-50/70 border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t("gh_capabilities_title")}
            </h2>
          </motion.div>

          <motion.div
            {...staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {CAPABILITIES.map((cap) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.titleKey}
                  {...staggerChild}
                  className={cn(
                    "group rounded-2xl border bg-white p-6 hover:shadow-md transition-all cursor-pointer",
                    cap.border
                  )}
                  onClick={() => navigate(cap.href)}
                >
                  <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center mb-4",
                    cap.bg
                  )}>
                    <Icon className={cn("w-5 h-5", cap.color)} />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {t(cap.titleKey)}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">
                    {t(cap.descKey)}
                  </p>
                  <span className={cn(
                    "inline-flex items-center text-sm font-medium transition-colors",
                    cap.color
                  )}>
                    {t(cap.ctaKey)}
                    <ChevronRight className={cn(
                      "w-4 h-4 ms-1 transition-transform group-hover:translate-x-0.5",
                      isRTL && "rotate-180 group-hover:-translate-x-0.5"
                    )} />
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 6 — LEARN (THE SCIENCE)
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("learn")}
        data-section="learn"
        id="learn"
        className="border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <LearnTabContent />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 7 — FINAL CTA
          ═══════════════════════════════════════════════════ */}
      <section
        ref={sectionRef("start")}
        data-section="start"
        id="start"
        className="relative overflow-hidden border-t border-gray-100"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-indigo-500/[0.03] to-violet-500/[0.05]" />

        <motion.div
          {...fadeUp}
          className="relative max-w-3xl mx-auto px-6 py-20 md:py-24 text-center"
        >
          <Brain className="w-10 h-10 text-primary/30 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            {t("gh_cta_title")}
          </h2>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            {t("gh_cta_subtitle")}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="px-7 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-shadow"
            >
              {t("gh_cta_button")}
              <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
            >
              {t("sign_in")}
            </Button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default GuestHome;
